import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Download, Zap, Shield, Gem, Wallet, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ethers } from 'ethers';
import BOOST15 from '../assets/boost15.png';
import BOOST30 from '../assets/boost30.png';
import BOOST50 from '../assets/boost50.png';
import { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Very Sans';
    src: url('/assets/fonts/VerySans-LightText.ttf') format('truetype');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans';
    src: url('/assets/fonts/VerySans-MediumText.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans';
    src: url('/assets/fonts/VerySans-BoldText.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Headline';
    src: url('/assets/fonts/VerySans-Headline.ttf') format('truetype');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
`;


const pulseGlow = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.8; }
`;

const NFTSection = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 25%, #2a1a4a 75%, #3a2a5a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  font-family: Very Sans, Sans Serif;
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  font-family: Very Sans, Sans Serif;
  @media (max-width: 768px) {
    padding: 0;
    margin: 0 auto;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  
  .gradient-text {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Description = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 32rem;
  margin: 0 auto;
  line-height: 1.6;
`;

const NFTGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    padding: 0 2.5vw;
  }
`;

const NFTCard = styled.div`
  background: rgba(26, 26, 46, 0.6);
  border: 1px solid rgba(139, 92, 246, 0.1);
  border-radius: 1rem;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  font-family: Very Sans, Sans Serif;
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    border-color: rgba(139, 92, 246, 0.3);
    background: rgba(26, 26, 46, 0.8);
    box-shadow: 0 20px 40px rgba(139, 92, 246, 0.2);
  }

  
    
  
`;

const CardHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 12rem;
  height: 12rem;
  margin: 0 auto 1rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background: linear-gradient(135deg, #1a1a2e, #2a1a4a);
  @media (max-width: 480px) {
    width: 8rem;
    height: 8rem;
  }
`;

const NFTImage = styled.img`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(167, 139, 250, 0.1));
  animation: ${pulseGlow} 2s ease-in-out infinite;
`;

const BadgeContainer = styled.div`
  margin-bottom: 0.5rem;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  
  ${props => {
    if (props.rarity === 'Common') return `
      background: rgba(139, 92, 246, 0.2);
      color: #a78bfa;
    `;
    if (props.rarity === 'Rare') return `
      background: linear-gradient(135deg, #00d4aa, #01b69d);
      color: white;
    `;
    if (props.rarity === 'Legendary') return `
      background: linear-gradient(135deg, #8b5cf6, #a78bfa);
      color: white;
    `;
  }}
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const BoostDisplay = styled.div`
  text-align: center;
`;

const BoostValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const BoostText = styled.span`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #00d4aa, #01b69d);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const BoostLabel = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
`;

const BenefitsSection = styled.div``;

const BenefitsTitle = styled.h4`
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const BenefitItem = styled.li`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
`;

const PriceSection = styled.div`
  padding-top: 1rem;
  border-top: 1px solid rgba(139, 92, 246, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Price = styled.div`
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
`;

const WalletInfo = styled.div`
  text-align: center;
  padding: 0.75rem;
  background: rgba(26, 26, 46, 0.4);
  border-radius: 0.5rem;
  border: 1px solid rgba(139, 92, 246, 0.1);
`;

const WalletText = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 0.5rem 0;
`;

const QuantityButton = styled.button`
  width: 2rem;
  height: 2rem;
  border-radius: 0.25rem;
  border: 1px solid rgba(139, 92, 246, 0.3);
  background: rgba(26, 26, 46, 0.6);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(139, 92, 246, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  min-width: 2rem;
  text-align: center;
`;

const DownloadButton = styled.button`
  width: 100%;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  border: none;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  ${props => {
    if (props.variant === 'connect') return `
      background: linear-gradient(135deg, #8b5cf6, #a78bfa);
      color: white;
      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #7c3aed, #8b5cf6);
        transform: translateY(-2px);
      }
    `;
    if (props.variant === 'mint') return `
      background: linear-gradient(135deg, #00d4aa, #01b69d);
      color: white;
      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #01b69d, #00a085);
        transform: translateY(-2px);
      }
    `;
    if (props.variant === 'rare') return `
      background: linear-gradient(135deg, #00d4aa, #01b69d);
      color: white;
      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #01b69d, #00a085);
        transform: translateY(-2px);
      }
    
    `;
    if (props.variant === 'legendary') return `
      background: linear-gradient(135deg, #8b5cf6, #a78bfa);
      color: white;
      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #7c3aed, #8b5cf6);
        transform: translateY(-2px);
      }
    `;
    return `
      background: rgba(139, 92, 246, 0.2);
      color: rgba(255, 255, 255, 0.6);
      border: 1px solid rgba(139, 92, 246, 0.1);
    `;
  }}
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 0.5rem;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #fca5a5;
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 0.5rem;
  padding: 0.75rem;
  color: #86efac;
  font-size: 0.875rem;
  text-align: center;
`;

const FooterInfo = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: rgba(26, 26, 46, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 0.5rem;
  border: 1px solid rgba(139, 92, 246, 0.1);
`;

const FooterText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  line-height: 1.6;
`;

// Contract configurations
const CONTRACTS = {
  BOOST15: {
    address: "0x530a966d49c93f4d0e65c583351364baf628f4e6",
    price: "0.1237"
  },
  BOOST30: {
    address: "0xfdebf79a44a81598b228044587f994401d5b4e4f",
    price: "0.21"
  },
  BOOST50: {
    address: "0xa821c34d755c35478746f48f3f63981722e210b5",
    price: "0.3"
  }
};

const AVAX_MAINNET_CHAIN_ID = "0xa86a"; // 43114 in hex (mainnet)

// Alternative RPC providers for fallback - MAINNET
const RPC_PROVIDERS = [
  'https://api.avax.network/ext/bc/C/rpc',
  'https://avalanche-c-chain.publicnode.com',
  'https://ava-mainnet.public.blastapi.io/ext/bc/C/rpc',
  'https://endpoints.omniatech.io/v1/avax/mainnet/public'
];

// Contract ABI - only the functions we need
const CONTRACT_ABI = [
  {
    "inputs": [{"internalType": "uint256", "name": "quantity", "type": "uint256"}],
    "name": "mint",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "saleActive",
    "outputs": [{"internalType": "bool", "name": "", "type": "bool"}],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "currentSupply",
    "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [{"internalType": "address", "name": "", "type": "address"}],
    "name": "mintedPerWallet",
    "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
    "stateMutability": "view",
    "type": "function"
  }
];

const NFTDownload = () => {
  const [account, setAccount] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  
  // Separate states for each contract
  const [boost15State, setBoost15State] = useState({
    isMinting: false,
    quantity: 1,
    error: '',
    success: '',
    saleActive: false,
    mintedCount: 0,
    totalSupply: 0
  });

  const [boost30State, setBoost30State] = useState({
    isMinting: false,
    quantity: 1,
    error: '',
    success: '',
    saleActive: false,
    mintedCount: 0,
    totalSupply: 0
  });

  const [boost50State, setBoost50State] = useState({
    isMinting: false,
    quantity: 1,
    error: '',
    success: '',
    saleActive: false,
    mintedCount: 0,
    totalSupply: 0
  });

  // Retry utility function
  const retryWithDelay = async (fn, maxRetries = 3, baseDelay = 1000) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await fn();
      } catch (error) {
        console.log(`Attempt ${i + 1} failed:`, error.message);
        
        if (i === maxRetries - 1) {
          throw error;
        }
        
        // Exponential backoff
        const delay = baseDelay * Math.pow(2, i);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  // Create provider with fallback
  const createProviderWithFallback = async () => {
    // Try MetaMask provider first
    if (window.ethereum) {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        // Test the connection
        await provider.getNetwork();
        return provider;
      } catch (error) {
        console.log('MetaMask provider failed, trying alternatives...');
      }
    }
    
    // Try alternative RPC providers
    for (const rpcUrl of RPC_PROVIDERS) {
      try {
        const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
        await provider.getNetwork();
        console.log(`Using fallback RPC: ${rpcUrl}`);
        return provider;
      } catch (error) {
        console.log(`RPC ${rpcUrl} failed, trying next...`);
        continue;
      }
    }
    
    throw new Error('All RPC providers failed');
  };

  // Check if MetaMask is installed
  const isMetaMaskInstalled = () => {
    // Desktop detection
    if (typeof window.ethereum !== 'undefined') {
      return true;
    }
    
    // Mobile detection - check user agent
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check if we're in MetaMask's in-app browser
    if (userAgent.includes('MetaMaskMobile')) {
      return true;
    }
    
    // On mobile, we can't detect if MetaMask is installed, so assume it might be
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    return isMobile; // On mobile, we'll try to connect and let MetaMask handle it
  };

  // Connect wallet - Enhanced version
  const connectWallet = async () => {
    setIsConnecting(true);
    setBoost15State(prev => ({...prev, error: ''}));
    setBoost30State(prev => ({...prev, error: ''}));
    setBoost50State(prev => ({...prev, error: ''}));

    try {
      // Check if we're on mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      const SITE_URL = 'hydrixapp.online/'; 

      // Mobile redirect to MetaMask
      if (isMobile && !window.ethereum) {
        const metamaskUrl = `https://metamask.app.link/dapp/${SITE_URL}`;
        window.location.href = metamaskUrl;
        return;
      }
      
      if (!window.ethereum) {
        setBoost15State(prev => ({...prev, error: 'Please install MetaMask or open this page in MetaMask browser'}));
        return;
      }

      // Request account access
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });

      if (accounts.length > 0) {
        setAccount(accounts[0]);
        
        // Switch to Avalanche mainnet if not already connected
        await switchToAvaxMainnet();
        
        // Load contract data with staggered delays
        setTimeout(() => loadContractData(accounts[0], 'BOOST15'), 0);
        setTimeout(() => loadContractData(accounts[0], 'BOOST30'), 500);
        setTimeout(() => loadContractData(accounts[0], 'BOOST50'), 1000);
      }
    } catch (err) {
      console.error('Error connecting wallet:', err);
      if (err.code === 4001) {
        setBoost15State(prev => ({...prev, error: 'Please accept the connection request in MetaMask'}));
      } else {
        setBoost15State(prev => ({...prev, error: 'Failed to connect wallet. Make sure you\'re using MetaMask browser on mobile.'}));
      }
    } finally {
      setIsConnecting(false);
    }
  };

  // Switch to Avalanche mainnet
  const switchToAvaxMainnet = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: AVAX_MAINNET_CHAIN_ID }]
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: AVAX_MAINNET_CHAIN_ID,
              chainName: 'Avalanche Network',
              nativeCurrency: {
                name: 'AVAX',
                symbol: 'AVAX',
                decimals: 18
              },
              rpcUrls: ['https://api.avax.network/ext/bc/C/rpc'],
              blockExplorerUrls: ['https://snowtrace.io/']
            }]
          });
        } catch (addError) {
          console.error('Error adding Avalanche network:', addError);
          setBoost15State(prev => ({...prev, error: 'Failed to add Avalanche network'}));
        }
      } else {
        console.error('Error switching to Avalanche:', switchError);
        setBoost15State(prev => ({...prev, error: 'Failed to switch to Avalanche network'}));
      }
    }
  };

  // Enhanced loadContractData function
  const loadContractData = async (userAccount, contractType) => {
    try {
      console.log(`Loading ${contractType} contract data for:`, userAccount);
      
      const loadData = async () => {
        const provider = await createProviderWithFallback();
        
        // Create contract instance
        const contract = new ethers.Contract(
          CONTRACTS[contractType].address,
          CONTRACT_ABI,
          provider
        );

        // Load data with individual try-catch for each call
        let saleStatus, supply, userMinted;
        
        try {
          saleStatus = await contract.saleActive();
          console.log(`${contractType} Sale status:`, saleStatus);
        } catch (error) {
          console.warn(`Failed to load sale status for ${contractType}:`, error.message);
          saleStatus = false; // Default fallback
        }

        try {
          supply = await contract.currentSupply();
          console.log(`${contractType} Total supply:`, supply.toString());
        } catch (error) {
          console.warn(`Failed to load supply for ${contractType}:`, error.message);
          supply = ethers.BigNumber.from(0); // Default fallback
        }

        try {
          userMinted = await contract.mintedPerWallet(userAccount);
          console.log(`${contractType} User minted count:`, userMinted.toString());
        } catch (error) {
          console.warn(`Failed to load user minted count for ${contractType}:`, error.message);
          userMinted = ethers.BigNumber.from(0); // Default fallback
        }

        return {
          saleActive: saleStatus,
          totalSupply: supply.toNumber(),
          mintedCount: userMinted.toNumber()
        };
      };

      // Retry the data loading with exponential backoff
      const data = await retryWithDelay(loadData, 3, 1000);

      // Update the appropriate state
      if (contractType === 'BOOST15') {
        setBoost15State(prev => ({
          ...prev,
          saleActive: data.saleActive,
          totalSupply: data.totalSupply,
          mintedCount: data.mintedCount,
          error: ''
        }));
      } else if (contractType === 'BOOST30') {
        setBoost30State(prev => ({
          ...prev,
          saleActive: data.saleActive,
          totalSupply: data.totalSupply,
          mintedCount: data.mintedCount,
          error: ''
        }));
      } else if (contractType === 'BOOST50') {
        setBoost50State(prev => ({
          ...prev,
          saleActive: data.saleActive,
          totalSupply: data.totalSupply,
          mintedCount: data.mintedCount,
          error: ''
        }));
      }

    } catch (err) {
      console.error(`Error loading ${contractType} contract data:`, err);
      
      // More user-friendly error messages
      let errorMsg = `Unable to load ${contractType} data. `;
      
      if (err.message.includes('missing trie node') || err.message.includes('state not available')) {
        errorMsg += 'Network sync issue - please try again in a moment.';
      } else if (err.message.includes('timeout') || err.message.includes('network')) {
        errorMsg += 'Network connection issue - please check your connection.';
      } else {
        errorMsg += 'Please try refreshing or check network connection.';
      }
      
      if (contractType === 'BOOST15') {
        setBoost15State(prev => ({...prev, error: errorMsg}));
      } else if (contractType === 'BOOST30') {
        setBoost30State(prev => ({...prev, error: errorMsg}));
      } else if (contractType === 'BOOST50') {
        setBoost50State(prev => ({...prev, error: errorMsg}));
      }
    }
  };

  // Mint NFT
  const mintNFT = async (contractType) => {
    if (!account || !window.ethereum) {
      const errorMsg = 'Please connect your wallet first';
      if (contractType === 'BOOST15') {
        setBoost15State(prev => ({...prev, error: errorMsg}));
      } else if (contractType === 'BOOST30') {
        setBoost30State(prev => ({...prev, error: errorMsg}));
      } else if (contractType === 'BOOST50') {
        setBoost50State(prev => ({...prev, error: errorMsg}));
      }
      return;
    }

    const currentState = contractType === 'BOOST15' ? boost15State : 
                        contractType === 'BOOST30' ? boost30State : boost50State;
    const setState = contractType === 'BOOST15' ? setBoost15State : 
                    contractType === 'BOOST30' ? setBoost30State : setBoost50State;

    if (!currentState.saleActive) {
      setState(prev => ({...prev, error: 'Sale is not currently active'}));
      return;
    }

    setState(prev => ({...prev, isMinting: true, error: '', success: ''}));

    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(CONTRACTS[contractType].address, CONTRACT_ABI, signer);

      // Calculate total cost
      const totalCost = ethers.utils.parseEther((parseFloat(CONTRACTS[contractType].price) * currentState.quantity).toString());

      // Call mint function
      const tx = await contract.mint(currentState.quantity, {
        value: totalCost,
        gasLimit: 300000
      });

      setState(prev => ({...prev, success: `Transaction submitted! Hash: ${tx.hash}`}));
      
      // Wait for transaction confirmation
      await tx.wait();
      
      setState(prev => ({...prev, success: 'NFT minted successfully!'}));
      
      // Reload contract data
      await loadContractData(account, contractType);
      
    } catch (err) {
      console.error(`Error minting ${contractType} NFT:`, err);
      let errorMsg = 'Minting failed';
      
      if (err.message.includes('user rejected')) {
        errorMsg = 'Transaction was rejected';
      } else if (err.message.includes('insufficient funds')) {
        errorMsg = 'Insufficient AVAX balance';
      } else {
        errorMsg = `Minting failed: ${err.message}`;
      }
      
      setState(prev => ({...prev, error: errorMsg}));
    } finally {
      setState(prev => ({...prev, isMinting: false}));
    }
  };

  // Update quantity for specific contract
  const updateQuantity = (contractType, newQuantity) => {
    if (contractType === 'BOOST15') {
      setBoost15State(prev => ({...prev, quantity: newQuantity}));
    } else if (contractType === 'BOOST30') {
      setBoost30State(prev => ({...prev, quantity: newQuantity}));
    } else if (contractType === 'BOOST50') {
      setBoost50State(prev => ({...prev, quantity: newQuantity}));
    }
  };

  // Check for account changes and auto-connect
  useEffect(() => {
    const checkConnection = async () => {
      if (window.ethereum) {
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts.length > 0) {
            setAccount(accounts[0]);
            // Staggered loading to prevent overwhelming RPC
            setTimeout(() => loadContractData(accounts[0], 'BOOST15'), 0);
            setTimeout(() => loadContractData(accounts[0], 'BOOST30'), 500);
            setTimeout(() => loadContractData(accounts[0], 'BOOST50'), 1000);
          }
        } catch (err) {
          console.error('Error checking connection:', err);
        }
      }
    };

    checkConnection();

    if (window.ethereum) {
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          // Staggered loading for account changes too
          setTimeout(() => loadContractData(accounts[0], 'BOOST15'), 0);
          setTimeout(() => loadContractData(accounts[0], 'BOOST30'), 500);
          setTimeout(() => loadContractData(accounts[0], 'BOOST50'), 1000);
        } else {
          setAccount('');
          setBoost15State(prev => ({...prev, mintedCount: 0, saleActive: false}));
          setBoost30State(prev => ({...prev, mintedCount: 0, saleActive: false}));
          setBoost50State(prev => ({...prev, mintedCount: 0, saleActive: false}));
        }
      });

      window.ethereum.on('chainChanged', () => {
        window.location.reload();
      });
    }
  }, []);

  


  



  return (
  <>
    <GlobalStyle />
    <NFTSection>
      <Container>
        <Header>
          <Title>
            <span className="gradient-text">Boost NFT Collection</span>
          </Title>
          <Description>
            Supercharge your mining operations with our exclusive booster NFT's. 
            Each NFT provides unique advantages and mining enhancements.
          </Description>
        </Header>

        <NFTGrid>
          {/* 15% Boost NFT */}
          <NFTCard>
            <CardHeader>
              <ImageContainer>
                <NFTImage src={BOOST15} />
              </ImageContainer>
              
              <BadgeContainer>
                <Badge rarity="Common">Common</Badge>
              </BadgeContainer>
              <CardTitle>Hydrix Boost 15</CardTitle>
            </CardHeader>

            <CardContent>
              <BoostDisplay>
                <BoostValue>
                  <Zap size={24} color="#00d4aa" />
                  <BoostText>15%</BoostText>
                </BoostValue>
                <BoostLabel>Mining Boost</BoostLabel>
              </BoostDisplay>

              <BenefitsSection>
                <BenefitsTitle>
                  <Gem size={16} color="#8b5cf6" style={{ marginRight: '0.5rem' }} />
                  Benefits
                </BenefitsTitle>
                <BenefitsList>
                  <BenefitItem>
                    <Shield size={12} color="#00d4aa" style={{ marginRight: '0.5rem' }} />
                    15% Mining Rewards
                  </BenefitItem>
                  <BenefitItem>
                    <Shield size={12} color="#00d4aa" style={{ marginRight: '0.5rem' }} />
                    15% Gem Conversion Boost
                  </BenefitItem>
                </BenefitsList>
              </BenefitsSection>

              <PriceSection>
                <Price>{CONTRACTS.BOOST15.price} AVAX</Price>
                
                {account && (
                  <WalletInfo>
                    <WalletText>
                      Connected: {`${account.slice(0, 6)}...${account.slice(-4)}`}
                    </WalletText>
                    <WalletText>
                      Minted: {boost15State.mintedCount}/20 | Supply: {boost15State.totalSupply}
                    </WalletText>
                  </WalletInfo>
                )}

                {boost15State.error && (
                  <ErrorMessage>
                    <AlertCircle size={16} />
                    {boost15State.error}
                  </ErrorMessage>
                )}

                {boost15State.success && (
                  <SuccessMessage>
                    {boost15State.success}
                  </SuccessMessage>
                )}

                {!account ? (
                  <DownloadButton 
                    onClick={connectWallet} 
                    disabled={isConnecting}
                    variant="connect"
                  >
                    <Wallet size={16} />
                    {isConnecting ? 'Connecting...' : 'Connect Wallet'}
                  </DownloadButton>
                ) : (
                  <>
                    <QuantitySelector>
                      <QuantityButton 
                        onClick={() => updateQuantity('BOOST15', Math.max(1, boost15State.quantity - 1))}
                        disabled={boost15State.quantity <= 1}
                      >
                        -
                      </QuantityButton>
                      <QuantityDisplay>{boost15State.quantity}</QuantityDisplay>
                      <QuantityButton 
                        onClick={() => updateQuantity('BOOST15', Math.min(5, boost15State.quantity + 1))}
                        disabled={boost15State.quantity >= 5 || boost15State.mintedCount + boost15State.quantity >= 20}
                      >
                        +
                      </QuantityButton>
                    </QuantitySelector>
                    
                    <DownloadButton 
                      onClick={() => mintNFT('BOOST15')}
                      disabled={boost15State.isMinting || !boost15State.saleActive || boost15State.mintedCount >= 20}
                      variant="mint"
                    >
                      <Download size={16} />
                      {boost15State.isMinting ? 'Minting...' : 
                       !boost15State.saleActive ? 'Sale Inactive' :
                       boost15State.mintedCount >= 20 ? 'Limit Reached' :
                       `Mint ${boost15State.quantity} NFT${boost15State.quantity > 1 ? 's' : ''}`}
                    </DownloadButton>
                  </>
                )}
              </PriceSection>
            </CardContent>
          </NFTCard>

          {/* 30% Boost NFT */}
          <NFTCard>
            <CardHeader>
              <ImageContainer>
                <NFTImage src={BOOST30} />
              </ImageContainer>
              
              <BadgeContainer>
                <Badge rarity="Rare">Rare</Badge>
              </BadgeContainer>
              <CardTitle>Hydrix Boost 30</CardTitle>
            </CardHeader>

            <CardContent>
              <BoostDisplay>
                <BoostValue>
                  <Zap size={24} color="#00d4aa" />
                  <BoostText>30%</BoostText>
                </BoostValue>
                <BoostLabel>Mining Boost</BoostLabel>
              </BoostDisplay>

              <BenefitsSection>
                <BenefitsTitle>
                  <Gem size={16} color="#8b5cf6" style={{ marginRight: '0.5rem' }} />
                  Benefits
                </BenefitsTitle>
                <BenefitsList>
                  <BenefitItem>
                    <Shield size={12} color="#00d4aa" style={{ marginRight: '0.5rem' }} />
                    30% Mining Rewards
                  </BenefitItem>
                  <BenefitItem>
                    <Shield size={12} color="#00d4aa" style={{ marginRight: '0.5rem' }} />
                    30% Gem Conversion Boost
                  </BenefitItem>
                </BenefitsList>
              </BenefitsSection>

              <PriceSection>
                <Price>{CONTRACTS.BOOST30.price} AVAX</Price>
                
                {account && (
                  <WalletInfo>
                    <WalletText>
                      Connected: {`${account.slice(0, 6)}...${account.slice(-4)}`}
                    </WalletText>
                    <WalletText>
                      Minted: {boost30State.mintedCount}/20 | Supply: {boost30State.totalSupply}
                    </WalletText>
                  </WalletInfo>
                )}

                {boost30State.error && (
                  <ErrorMessage>
                    <AlertCircle size={16} />
                    {boost30State.error}
                  </ErrorMessage>
                )}

                {boost30State.success && (
                  <SuccessMessage>
                    {boost30State.success}
                  </SuccessMessage>
                )}

                {!account ? (
                  <DownloadButton 
                    onClick={connectWallet} 
                    disabled={isConnecting}
                    variant="connect"
                  >
                    <Wallet size={16} />
                    {isConnecting ? 'Connecting...' : 'Connect Wallet'}
                  </DownloadButton>
                ) : (
                  <>
                    <QuantitySelector>
                      <QuantityButton 
                        onClick={() => updateQuantity('BOOST30', Math.max(1, boost30State.quantity - 1))}
                        disabled={boost30State.quantity <= 1}
                      >
                        -
                      </QuantityButton>
                      <QuantityDisplay>{boost30State.quantity}</QuantityDisplay>
                      <QuantityButton 
                        onClick={() => updateQuantity('BOOST30', Math.min(5, boost30State.quantity + 1))}
                        disabled={boost30State.quantity >= 5 || boost30State.mintedCount + boost30State.quantity >= 20}
                      >
                        +
                      </QuantityButton>
                    </QuantitySelector>
                    
                    <DownloadButton 
                      onClick={() => mintNFT('BOOST30')}
                      disabled={boost30State.isMinting || !boost30State.saleActive || boost30State.mintedCount >= 20}
                      variant="rare"
                    >
                      <Download size={16} />
                      {boost30State.isMinting ? 'Minting...' : 
                       !boost30State.saleActive ? 'Sale Inactive' :
                       boost30State.mintedCount >= 20 ? 'Limit Reached' :
                       `Mint ${boost30State.quantity} NFT${boost30State.quantity > 1 ? 's' : ''}`}
                    </DownloadButton>
                  </>
                )}
              </PriceSection>
            </CardContent>
          </NFTCard>

          {/* 50% Boost NFT - Coming Soon */}
          {/* 50% Boost NFT */}
          <NFTCard>
            <CardHeader>
              <ImageContainer>
                <NFTImage src={BOOST50} />
              </ImageContainer>
              
              <BadgeContainer>
                <Badge rarity="Legendary">Legendary</Badge>
              </BadgeContainer>
              <CardTitle>Hydrix Boost 50</CardTitle>
            </CardHeader>

            <CardContent>
              <BoostDisplay>
                <BoostValue>
                  <Zap size={24} color="#00d4aa" />
                  <BoostText>50%</BoostText>
                </BoostValue>
                <BoostLabel>Mining Boost</BoostLabel>
              </BoostDisplay>

              <BenefitsSection>
                <BenefitsTitle>
                  <Gem size={16} color="#8b5cf6" style={{ marginRight: '0.5rem' }} />
                  Benefits
                </BenefitsTitle>
                <BenefitsList>
                  <BenefitItem>
                    <Shield size={12} color="#00d4aa" style={{ marginRight: '0.5rem' }} />
                    50% Mining Rewards
                  </BenefitItem>
                  <BenefitItem>
                    <Shield size={12} color="#00d4aa" style={{ marginRight: '0.5rem' }} />
                    50% Gem Conversion Boost
                  </BenefitItem>
                </BenefitsList>
              </BenefitsSection>

              <PriceSection>
                <Price>{CONTRACTS.BOOST50.price} AVAX</Price>
                
                {account && (
                  <WalletInfo>
                    <WalletText>
                      Connected: {`${account.slice(0, 6)}...${account.slice(-4)}`}
                    </WalletText>
                    <WalletText>
                      Minted: {boost50State.mintedCount}/20 | Supply: {boost50State.totalSupply}
                    </WalletText>
                  </WalletInfo>
                )}

                {boost50State.error && (
                  <ErrorMessage>
                    <AlertCircle size={16} />
                    {boost50State.error}
                  </ErrorMessage>
                )}

                {boost50State.success && (
                  <SuccessMessage>
                    {boost50State.success}
                  </SuccessMessage>
                )}

                {!account ? (
                  <DownloadButton 
                    onClick={connectWallet} 
                    disabled={isConnecting}
                    variant="connect"
                  >
                    <Wallet size={16} />
                    {isConnecting ? 'Connecting...' : 'Connect Wallet'}
                  </DownloadButton>
                ) : (
                  <>
                    <QuantitySelector>
                      <QuantityButton 
                        onClick={() => updateQuantity('BOOST50', Math.max(1, boost50State.quantity - 1))}
                        disabled={boost50State.quantity <= 1}
                      >
                        -
                      </QuantityButton>
                      <QuantityDisplay>{boost50State.quantity}</QuantityDisplay>
                      <QuantityButton 
                        onClick={() => updateQuantity('BOOST50', Math.min(5, boost50State.quantity + 1))}
                        disabled={boost50State.quantity >= 5 || boost50State.mintedCount + boost50State.quantity >= 20}
                      >
                        +
                      </QuantityButton>
                    </QuantitySelector>
                    
                    <DownloadButton 
                      onClick={() => mintNFT('BOOST50')}
                      disabled={boost50State.isMinting || !boost50State.saleActive || boost50State.mintedCount >= 20}
                      variant="legendary"
                    >
                      <Download size={16} />
                      {boost50State.isMinting ? 'Minting...' : 
                      !boost50State.saleActive ? 'Sale Inactive' :
                      boost50State.mintedCount >= 20 ? 'Limit Reached' :
                      `Mint ${boost50State.quantity} NFT${boost50State.quantity > 1 ? 's' : ''}`}
                    </DownloadButton>
                  </>
                )}
              </PriceSection>
            </CardContent>
          </NFTCard>
        </NFTGrid>

        <FooterInfo>
          <FooterText>
            All NFTs are compatible with Hydrix mining protocols. 
            Download includes metadata, high-resolution artwork, and smart contract verification.
          </FooterText>
        </FooterInfo>
      </Container>
    </NFTSection>
    
  </>
  );
};

export default NFTDownload;